// localForage (IndexedDB) wrapper for "My Sheets" and the project planner.
// Browser-only — all guards check typeof window before touching localForage.
import localforage from 'localforage'

// ─── Saved sheets (calculator results) ────────────────────────────────────────

export interface SavedSheet {
  id: string
  type: string // calculator slug
  name: string
  createdAt: string
  summary: string
  lines?: { label: string; qty?: number; unit?: string }[]
}

let sheetsStore: LocalForage | null = null
function db(): LocalForage | null {
  if (typeof window === 'undefined') return null
  if (!sheetsStore) sheetsStore = localforage.createInstance({ name: 'renosheets', storeName: 'sheets' })
  return sheetsStore
}

export function genId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7)
}

export async function saveSheet(s: SavedSheet): Promise<void> {
  const d = db()
  if (d) await d.setItem(s.id, s)
}

export async function listSheets(): Promise<SavedSheet[]> {
  const d = db()
  if (!d) return []
  const out: SavedSheet[] = []
  await d.iterate<SavedSheet, void>((v) => { out.push(v) })
  return out.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export async function deleteSheet(id: string): Promise<void> {
  const d = db()
  if (d) await d.removeItem(id)
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export interface Project {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

let projectsStore: LocalForage | null = null
function pdb(): LocalForage | null {
  if (typeof window === 'undefined') return null
  if (!projectsStore) projectsStore = localforage.createInstance({ name: 'renosheets', storeName: 'projects' })
  return projectsStore
}

export async function saveProject(p: Project): Promise<void> {
  const d = pdb()
  if (d) await d.setItem(p.id, p)
}

export async function getProject(id: string): Promise<Project | null> {
  const d = pdb()
  return d ? d.getItem<Project>(id) : null
}

export async function listProjects(): Promise<Project[]> {
  const d = pdb()
  if (!d) return []
  const out: Project[] = []
  await d.iterate<Project, void>((v) => { out.push(v) })
  return out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export async function deleteProject(id: string): Promise<void> {
  const d = pdb()
  const t = tdb()
  if (d) await d.removeItem(id)
  // also remove all tasks belonging to this project
  if (t) {
    const toDelete: string[] = []
    await t.iterate<TaskCard, void>((v) => { if (v.projectId === id) toDelete.push(v.id) })
    for (const tid of toDelete) await t.removeItem(tid)
  }
}

// Create a new project and seed it with a set of template tasks (in one call).
// Returns the new project id. Tasks default to the 'planning' column.
export async function createProjectFromTemplate(
  name: string,
  tasks: { name: string; category: TaskCategory; calculatorType?: string; column?: Column }[],
): Promise<string> {
  const now = new Date().toISOString()
  const id = genId()
  await saveProject({ id, name, createdAt: now, updatedAt: now })
  const counts: Partial<Record<Column, number>> = {}
  for (const t of tasks) {
    const column = t.column ?? 'planning'
    const sortOrder = counts[column] ?? 0
    counts[column] = sortOrder + 1
    await saveTask({
      id: genId(), projectId: id, name: t.name,
      category: t.category, column,
      calculatorType: t.calculatorType ?? '', summary: '',
      estimatedCost: 0, note: '', sortOrder,
      createdAt: now, updatedAt: now,
    })
  }
  return id
}

// ─── Task cards ───────────────────────────────────────────────────────────────

export const COLUMNS = [
  { id: 'planning' as const, label: 'Planning', description: 'Researching — not yet bought' },
  { id: 'quoted' as const, label: 'Quoted', description: 'Got prices or contractor quotes' },
  { id: 'inprogress' as const, label: 'In Progress', description: 'Materials bought, work underway' },
  { id: 'done' as const, label: 'Done', description: 'Completed' },
]
export type Column = typeof COLUMNS[number]['id']

export const TASK_CATEGORIES = [
  'paint', 'flooring', 'tile', 'concrete', 'drywall',
  'electrical', 'plumbing', 'kitchen', 'bathroom',
  'outdoor', 'roofing', 'structural', 'other',
] as const
export type TaskCategory = typeof TASK_CATEGORIES[number]

export interface TaskCard {
  id: string
  projectId: string
  name: string
  category: TaskCategory
  column: Column
  calculatorType: string       // calculator slug, e.g. 'tile', or '' for manual tasks
  // ID of the SavedSheet this task was imported from. If present, the task's
  // displayed summary/lines are pulled LIVE from the saved sheet at render
  // time — so editing the saved sheet propagates everywhere it's attached.
  // Falls back to the cached summary/lines below if the sheet was deleted.
  sheetId?: string
  summary: string              // cached snapshot — used as fallback if sheetId resolves to nothing
  lines?: { label: string; qty?: number; unit?: string }[]   // cached snapshot — same fallback role
  estimatedCost: number        // user-entered, dollars
  note: string
  dueDate?: string             // ISO date (yyyy-mm-dd), or undefined / '' for none
  color?: string               // colour key for the card (see CARD_COLORS), '' = none
  sortOrder: number
  createdAt: string
  updatedAt: string
}

let tasksStore: LocalForage | null = null
function tdb(): LocalForage | null {
  if (typeof window === 'undefined') return null
  if (!tasksStore) tasksStore = localforage.createInstance({ name: 'renosheets', storeName: 'tasks' })
  return tasksStore
}

export async function saveTask(t: TaskCard): Promise<void> {
  const d = tdb()
  if (d) await d.setItem(t.id, t)
  // bump project updatedAt
  const proj = await getProject(t.projectId)
  if (proj) await saveProject({ ...proj, updatedAt: new Date().toISOString() })
}

export async function getTask(id: string): Promise<TaskCard | null> {
  const d = tdb()
  return d ? d.getItem<TaskCard>(id) : null
}

export async function listTasksByProject(projectId: string): Promise<TaskCard[]> {
  const d = tdb()
  if (!d) return []
  const out: TaskCard[] = []
  await d.iterate<TaskCard, void>((v) => { if (v.projectId === projectId) out.push(v) })
  return out.sort((a, b) => {
    const oa = a.sortOrder ?? 0
    const ob = b.sortOrder ?? 0
    if (oa !== ob) return oa - ob
    return a.createdAt.localeCompare(b.createdAt)
  })
}

export async function deleteTask(id: string): Promise<void> {
  const d = tdb()
  if (d) await d.removeItem(id)
}

export async function moveTask(id: string, column: Column): Promise<void> {
  const d = tdb()
  if (!d) return
  const t = await d.getItem<TaskCard>(id)
  if (t) await d.setItem(id, { ...t, column, updatedAt: new Date().toISOString() })
}

// All task cards across every project (used for backup export).
export async function listAllTasks(): Promise<TaskCard[]> {
  const d = tdb()
  if (!d) return []
  const out: TaskCard[] = []
  await d.iterate<TaskCard, void>((v) => { out.push(v) })
  return out
}

// ─── Backup / restore (export & import everything) ──────────────────────────

export interface BackupData {
  app: 'renosheets'
  version: number
  exportedAt: string
  projects: Project[]
  tasks: TaskCard[]
  sheets: SavedSheet[]
}

export async function exportBackup(): Promise<BackupData> {
  const [projects, sheets, tasks] = await Promise.all([listProjects(), listSheets(), listAllTasks()])
  return { app: 'renosheets', version: 1, exportedAt: new Date().toISOString(), projects, tasks, sheets }
}

// Merge an imported backup into local storage (overwrites items with the same id,
// keeps everything else). Returns how many records of each type were written.
export async function importBackup(data: BackupData): Promise<{ projects: number; tasks: number; sheets: number }> {
  if (!data || data.app !== 'renosheets' || !Array.isArray(data.projects)) {
    throw new Error('This file is not a RenoSheets backup.')
  }
  const pd = pdb(), td = tdb(), sd = db()
  let projects = 0, tasks = 0, sheets = 0
  if (pd) for (const p of data.projects ?? []) { await pd.setItem(p.id, p); projects++ }
  if (td) for (const t of data.tasks ?? []) { await td.setItem(t.id, t); tasks++ }
  if (sd) for (const s of data.sheets ?? []) { await sd.setItem(s.id, s); sheets++ }
  return { projects, tasks, sheets }
}
